import React, { useEffect, useState } from 'react';
import { Badge, Box, IconButton, IconButtonProps, Tooltip, VisuallyHidden } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRotateRight,
  faCheck,
  faCloudUpload,
  faHourglass,
  faFile,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { DocStatusType, FollowUpDocStatusType, IOption } from 'api-paths-cas';

import Finger, { Status } from '../finger-doc';
import { UserRole } from '../../../store/auth/types';
import { FingerWithButtons } from '../../Finger/FingerWithButtons';
import { shallowEqual, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { API_CALLS } from '../../../utilities/constants';

interface Props extends React.HTMLProps<HTMLDivElement> {
  id?: string;
  status: Status;
  isProcessingPdf?: boolean;
  docStatusType?: DocStatusType | FollowUpDocStatusType;
  docStatusDropdownOptions?: Array<IOption>;
  docType?: string;
  isDocumentFixed?: boolean;
  reason?: string;
  description?: string;
  label: string;
  labelBtn?: React.ReactElement;
  fileNames?: string[];
  fileCount?: number;
  hasImages?: boolean;
  buttonLabel?: string;
  resolveLabel?: string;
  children?: React.ReactNode;
  userRole?: UserRole;
  canCheckOff?: boolean;
  canUpload?: boolean;
  canOpen: boolean;
  canApprove?: boolean;
  canReject?: boolean;
  isDeleting: boolean;
  canAcknowledge?: boolean;
  isDocSetup?: boolean;
  questionHidden?: boolean;
  canAttach?: boolean;
  canPass?: boolean;
  canResit?: boolean;
  canFail?: boolean;
  notDocumentFinger?: boolean;
  deletedByTTL?: boolean;
  notBeReceivingReasonExists?: boolean;

  isCheckedOff?: () => boolean;
  onCheckOffRisk?: () => void;
  onUploadClick?: () => void;
  onOpenClick: () => void;
  onApproveClick?: () => void;
  onRejectClick?: () => void;
  onRescindRejectClick?: () => void;
  onDeleteClick: () => void;
  onAcknowledgeClick?: () => void;
  onAttachClick?: () => void;
  onPassClick?: () => void;
  onResitClick?: () => void;
  onFailClick?: () => void;
  onClick?: () => void;
  onChangeStatusClick?: (label: string, status: string) => void;
  onToggleStatus?: () => void;
  onQuestionHiddenChange?: (hidden: boolean) => void;
}

const FingerWithAction: React.FC<Props> = (props: Props) => {
  const {
    id = 'finger',
    status,
    isProcessingPdf = false,
    isDocumentFixed = false,
    reason,
    description,
    label,
    labelBtn,
    userRole,
    fileCount = 0,
    canUpload = true,
    canOpen = true,
    canApprove = true,
    canReject = true,
    canAcknowledge = false,
    canAttach = true,
    canPass = true,
    canResit = true,
    canFail = true,
    isDeleting = false,
    notBeReceivingReasonExists = false,
    onUploadClick,
    onOpenClick,
    onApproveClick,
    onRejectClick,
    onRescindRejectClick,
    onDeleteClick,
    onAcknowledgeClick,
    onAttachClick,
    onPassClick,
    onResitClick,
    onFailClick,
    children,
    isCheckedOff,
    onCheckOffRisk,
    canCheckOff = false,
    notDocumentFinger,
    deletedByTTL = false,
  } = props;

  const { data } = useSelector(({ data }: ApplicationState) => ({ data }), shallowEqual);
  const isUploadedWithNoDocs = status === Status.UploadedOK && fileCount === 0;
  const isProcessingDocs = isProcessingPdf || isUploadedWithNoDocs;
  const isRejected = [Status.Rejected, Status.RejectedWithoutAlt].includes(status);

  const isAwaitingOrNoFiles = !isProcessingDocs && (status === Status.Awaiting || fileCount === 0);
  const hasReceivingIssuesAndNoFiles = notBeReceivingReasonExists && fileCount === 0;
  const shouldShowIconContainer = isAwaitingOrNoFiles || notDocumentFinger || hasReceivingIssuesAndNoFiles;

  const buttonMargin = { base: 2, xl: 3 };

  const [componentsLoading, setComponentsLoading] = useState<Array<string>>([]);

  useEffect(() => {
    const approvalApiCallsInProgress = (
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_APPROVE)?.componentIds ?? []
    ).map(x => `${x}-action-approve-button`);
    const rejectApiCallsInProgress =
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_REJECT)?.componentIds ?? [];
    const rescindApproveApiCallsInProgress =
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_RESCIND_APPROVE)?.componentIds ?? [];
    const rescindFailApiCallsInProgress = (
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_RESCIND_FAIL)?.componentIds ?? []
    ).map(x => `${x}-action-unfail-button`);
    const rescindPassApiCallsInProgress =
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_RESCIND_PASS)?.componentIds ?? [];
    const deleteApiCallsInProgress =
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_DELETE)?.componentIds ?? [];
    const acknowledgeApiCallsInProgress = (
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_ACKNOWLEDGE)?.componentIds ?? []
    ).map(x => `${x}-action-acknowledge-button`);
    const passApiCallsInProgress = (
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_PASS)?.componentIds ?? []
    ).map(x => `${x}-action-pass-button`);
    const resitApiCallsInProgress = (
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_RESIT)?.componentIds ?? []
    ).map(x => `${x}-action-resit-button`);
    const resitRescindApiCallsInProgress = (
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_RESCIND_RESIT)?.componentIds ?? []
    ).map(x => `${x}-action-rescind-resit-button`);
    const failApiCallsInProgress = (
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_FAIL)?.componentIds ?? []
    ).map(x => `${x}-action-fail-button`);
    const checkOffRiskApiCallsInProgress =
      data.apiCallsInProgress.find(x => x.callName === API_CALLS.FINGER_CHECK_OFF_RISK)?.componentIds ?? [];

    setComponentsLoading([
      ...approvalApiCallsInProgress,
      ...rejectApiCallsInProgress,
      ...rescindApproveApiCallsInProgress,
      ...rescindPassApiCallsInProgress,
      ...rescindFailApiCallsInProgress,
      ...deleteApiCallsInProgress,
      ...acknowledgeApiCallsInProgress,
      ...passApiCallsInProgress,
      ...resitApiCallsInProgress,
      ...failApiCallsInProgress,
      ...rescindFailApiCallsInProgress,
      ...checkOffRiskApiCallsInProgress,
      ...resitRescindApiCallsInProgress,
    ]);
  }, [data]);

  const isButtonLoading = (componentId: string): boolean => {
    return componentsLoading.includes(componentId);
  };
  const IconContainer = ({ children, hasMargin }: { children?: React.ReactNode; hasMargin?: boolean }) => {
    return (
      <Box width={10} ml={hasMargin ? buttonMargin : null}>
        {children}
      </Box>
    );
  };

  const FingerIconButton = ({
    title,
    icon,
    isActive,
    isDisabled,
    isLoading,
    colorScheme,
    onClick,
    hasCounter,
    count,
  }: {
    title: string;
    icon: IconProp;
    isActive?: boolean;
    isDisabled?: boolean;
    isLoading?: boolean;
    colorScheme?: IconButtonProps['colorScheme'];
    onClick?: () => void;
    hasCounter?: boolean;
    count?: number;
  }) => {
    return (
      <Tooltip hasArrow placement="top" label={title}>
        <Box position={'relative'}>
          <IconButton
            aria-label={title}
            icon={<FontAwesomeIcon icon={icon} />}
            isActive={isActive}
            isDisabled={isDisabled}
            isLoading={isLoading}
            onClick={onClick}
            colorScheme={colorScheme}
            _focus={{ zIndex: 'initial' }}
          />

          {hasCounter ? (
            <Badge
              variant={'vivid-pill'}
              colorScheme={count > 0 ? 'blue' : 'gray'}
              position={'absolute'}
              top={-1}
              right={-1}
              pointerEvents={'none'}
            >
              {count}
              <VisuallyHidden> {title} items</VisuallyHidden>
            </Badge>
          ) : null}
        </Box>
      </Tooltip>
    );
  };

  const FingerActions = () => {
    return (
      <>
        {canAcknowledge && onAcknowledgeClick && (
          <Box data-selector={`${id}-action-acknowledge`}>
            {status === Status.Acknowledged || status === Status.InfoOnly || status === Status.NotAcknowledged ? (
              <FingerIconButton
                title="Acknowledged"
                onClick={onAcknowledgeClick}
                icon={faCheck}
                isActive={status === Status.Acknowledged}
                colorScheme="green"
                isLoading={isButtonLoading(`${id}-action-acknowledge-button`)}
              />
            ) : (
              <IconContainer />
            )}
          </Box>
        )}

        {labelBtn && <Box mr={buttonMargin}>{labelBtn}</Box>}
        {canOpen && !deletedByTTL && (
          <Box key={`${id}${isProcessingDocs ? '-processing' : ''}`} data-selector={`${id}-action-open`}>
            {shouldShowIconContainer ? (
              <IconContainer />
            ) : (
              <FingerIconButton
                title={isProcessingDocs ? 'Processing...' : 'Preview'}
                icon={isProcessingDocs ? faHourglass : faFile}
                key={`${id}${isProcessingDocs ? '-processing' : ''}`}
                onClick={isProcessingDocs ? () => null : onOpenClick}
                isDisabled={isProcessingDocs}
                hasCounter={fileCount > 0}
                count={fileCount}
              />
            )}
          </Box>
        )}

        {canUpload && !deletedByTTL && onUploadClick && (
          <Box data-selector={`${id}-action-upload`}>
            {status === Status.UploadedOK ||
            status === Status.Awaiting ||
            status === Status.UploadedError ||
            status === Status.InError ||
            status === Status.Skipped ||
            status === Status.Approved ||
            status === Status.Replaced ? (
              <FingerIconButton title="Upload" icon={faCloudUpload} onClick={onUploadClick} />
            ) : (
              <IconContainer />
            )}
          </Box>
        )}

        {canAttach && onAttachClick && (
          <Box data-selector={`${id}-action-attach`}>
            {status === Status.Requested ||
            status === Status.Booked ||
            status === Status.Attempted ||
            status === Status.Recorded ||
            status === Status.Passed ||
            status === Status.Failed ||
            status === Status.Resit ? (
              <FingerIconButton title="Attach" icon={faCloudUpload} onClick={onAttachClick} />
            ) : (
              <IconContainer />
            )}
          </Box>
        )}

        {canApprove && onApproveClick && (
          <Box data-selector={`${id}-action-approve`}>
            {status === Status.Replaced || status === Status.Skipped || isRejected ? (
              <IconContainer hasMargin />
            ) : (
              <IconContainer hasMargin>
                <FingerIconButton
                  title="Approve"
                  icon={faCheck}
                  onClick={onApproveClick}
                  isActive={status === Status.Approved}
                  colorScheme="green"
                  isLoading={isButtonLoading(`${id}-action-approve-button`)}
                />
              </IconContainer>
            )}
          </Box>
        )}

        {canPass && onPassClick && (
          <Box data-selector={`${id}-action-pass`}>
            {status === Status.Cancelled || status === Status.Failed ? (
              <IconContainer hasMargin />
            ) : (
              <IconContainer hasMargin>
                <FingerIconButton
                  title="Pass"
                  icon={faCheck}
                  onClick={onPassClick}
                  isActive={status === Status.Passed}
                  colorScheme="green"
                  isLoading={isButtonLoading(`${id}-action-pass-button`)}
                />
              </IconContainer>
            )}
          </Box>
        )}

        {canResit && onResitClick && (
          <Box data-selector={`${id}-action-resit`}>
            {status === Status.Cancelled || status === Status.Failed ? (
              <IconContainer />
            ) : (
              <FingerIconButton
                title="Resit"
                icon={faArrowRotateRight}
                onClick={onResitClick}
                isActive={status === Status.Resit}
                colorScheme="orange"
                isLoading={isButtonLoading(
                  status === Status.Resit ? `${id}-action-rescind-resit-button` : `${id}-action-resit-button`,
                )}
              />
            )}
          </Box>
        )}

        {canReject && onRejectClick && (
          <Box data-selector={`${id}-action-reject`}>
            {status === Status.Replaced || status === Status.Skipped ? (
              <IconContainer />
            ) : (
              <FingerIconButton
                title={isRejected ? 'Rescind Rejection' : 'Reject'}
                icon={faXmark}
                isActive={isRejected}
                onClick={isRejected ? onRescindRejectClick : onRejectClick}
                colorScheme="red"
                isLoading={isButtonLoading(`${id}-action-${isRejected ? 'rescind' : 'reject'}-button`)}
              />
            )}
          </Box>
        )}

        {canFail && onFailClick && (
          <Box data-selector={`${id}-action-fail`}>
            {status === Status.Cancelled ? (
              <IconContainer />
            ) : (
              <FingerIconButton
                title={status === Status.Failed ? 'Unfail' : 'Fail'}
                icon={faXmark}
                isActive={status === Status.Failed}
                onClick={onFailClick}
                colorScheme="red"
                isLoading={isButtonLoading(
                  status === Status.Failed ? `${id}-action-unfail-button` : `${id}-action-fail-button`,
                )}
              />
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <FingerWithButtons actions={<FingerActions />} className="finger-with-approve" data-selector={`${id}-div`}>
      <Finger
        id={id}
        status={status}
        isDocumentFixed={isDocumentFixed}
        isDeleting={isDeleting}
        reason={reason}
        description={description}
        label={label}
        labelBtn={labelBtn}
        canCheckOff={canCheckOff}
        onCheckOffRisk={onCheckOffRisk}
        isCheckedOff={isCheckedOff && isCheckedOff()}
        onDeleteClick={onDeleteClick}
        userRole={userRole}
        deletedByTTL={deletedByTTL}
      >
        {children}
      </Finger>
    </FingerWithButtons>
  );
};

export default FingerWithAction;
